import type { NumberLocale } from "../core/parser/locale";
import { parseAmount, parseDate } from "../core/parser/locale";
import type { BalanceInfo, BankTemplate, ColumnLayout } from "../core/parser/template";
import { textInXRange } from "../core/pdf/lines";
import type { TextLine } from "../core/types";

// OTP banka Srbija a.d. — RSD statement ("Izvod"). Reverse-engineered from a
// real statement.
//
// Layout (PDF points): Datum knjiženja | Datum valute | Uplate (in) |
//   Isplate (out) | Opis | Stanje. Note the description column sits to the RIGHT
//   of the amount columns. Numbers are Serbian ("600.000,00" = comma decimal /
//   dot thousands). Opening = "Početno stanje", closing = "Stanje na računu".

export const OTP_RS_LOCALE: NumberLocale = {
  decimalSeparator: ",",
  thousandsSeparator: ".",
};

export const OTP_RS_COLUMNS: ColumnLayout = {
  date: { xMin: 25, xMax: 82 }, // Datum knjiženja (booking)
  valueDate: { xMin: 82, xMax: 145 }, // Datum valute
  credit: { xMin: 145, xMax: 220 }, // Uplate (money in)
  debit: { xMin: 220, xMax: 268 }, // Isplate (money out)
  description: { xMin: 268, xMax: 480 }, // Opis (right of the amount columns)
  balance: { xMin: 480, xMax: 580 }, // Stanje (running balance)
};

const DATE_CELL = /^\d{2}\.\d{2}\.\d{4}$/;

export const otpBankaRsTemplate: BankTemplate = {
  id: "rs-otp",
  name: "OTP banka Srbija",
  country: "RS",
  currency: "RSD",
  numberLocale: OTP_RS_LOCALE,
  dateFormats: ["DD.MM.YYYY"],
  layout: OTP_RS_COLUMNS,
  dateCellPattern: DATE_CELL,
  stopRowPattern:
    /Datum i vreme|www\.|Vaša OTP|Info center|\d+\s+od\s+\d+|Datum knji|Datum valute|Uplate|Isplate|Po[cč]etno stanje|Stanje na|Naziv ra|Interna [šs]ifra|Datum slanja|Izvod broj|za period/i,

  detect(fullText: string): number {
    const t = fullText.toLowerCase();
    let score = 0;
    if (t.includes("otp banka")) score += 0.6;
    if (t.includes("otpbanka")) score += 0.3;
    if (t.includes("izvod broj")) score += 0.1;
    return Math.min(score, 1);
  },

  extractBalances(lines: TextLine[], locale: NumberLocale): BalanceInfo {
    const full = lines.map((l) => l.text).join("\n");

    const opening = matchAmount(full, /po[cč]etno stanje\s*:?\s*(-?[\d.,]+)/i, locale);
    const closing =
      matchAmount(full, /stanje na ra[cč]unu\s*:?\s*(-?[\d.,]+)/i, locale) ??
      lastRowBalance(lines, locale);

    const period =
      /za period\s*:?\s*(\d{2}\.\d{2}\.\d{4})\s*-\s*(\d{2}\.\d{2}\.\d{4})/i.exec(full);
    const account = /\bra[cč]un\s*:?\s*(\d[\d-]+)/i.exec(full);

    return {
      opening,
      closing,
      periodStart: period ? parseDate(period[1]!, ["DD.MM.YYYY"]) ?? undefined : undefined,
      periodEnd: period ? parseDate(period[2]!, ["DD.MM.YYYY"]) ?? undefined : undefined,
      accountId: account ? account[1] : undefined,
    };
  },
};

/** Running balance of the last transaction row — fallback closing balance. */
function lastRowBalance(lines: TextLine[], locale: NumberLocale): number | null {
  const B = OTP_RS_COLUMNS.balance!;
  const D = OTP_RS_COLUMNS.date;
  let last: number | null = null;
  for (const line of lines) {
    if (!DATE_CELL.test(textInXRange(line, D.xMin, D.xMax))) continue;
    const bal = parseAmount(textInXRange(line, B.xMin, B.xMax), locale);
    if (bal != null) last = bal;
  }
  return last;
}

function matchAmount(text: string, re: RegExp, locale: NumberLocale): number | null {
  const m = re.exec(text);
  if (!m || !m[1]) return null;
  return parseAmount(m[1], locale);
}
